import React, { useState, useCallback } from "react";
import { Box } from "@mui/material";
import { GridCellEditStartReasons, DataGrid, GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid";
import { v4 } from "uuid";
import "../../alerts/InfoMessage.js";
import "../../alerts/NoAccessInfoMessage.js";
import "../../alerts/Message.style.js";
import "react-intl";
import { InfoAlert } from "../../alerts/InfoAlert.js";
import "../../alerts/InfoMessageHoc.js";
import { StyledCheckbox } from "../../checkbox/StyledCheckbox.js";
import { useGridRowSelection } from "./hooks/useGridRowSelection.js";
import StyledPagination from "../../pagination/StyledPagination.js";
import { GridColumnsButton } from "./GridColumnsButton.js";
import { GridColumnsPanel } from "./GridColumnsPanel.js";
import "./hooks/useGridCellStyle.js";
import { useGridLocalization } from "./hooks/useGridLocalization.js";
import { useGridPaging } from "./hooks/useGridPaging.js";
import { useGridSorting } from "./hooks/useGridSorting.js";
import { useGridStyle } from "./hooks/useGridStyle.js";
import { useRows } from "./hooks/useRows.js";
import { useColumns } from "./hooks/useColumns.js";
const defaultRowHeight = 48;
const MuiDataGrid = (props) => {
  const {
    pinnedColumns,
    pinnedRow,
    visibleColumns,
    hasColumnSelection,
    isFlexible,
    hideRows,
    hideHeader,
    emptyMessage,
    style,
    className,
    noToolbarMargin,
    hidePagination,
    slots
  } = props;
  const [gridId] = useState(v4());
  const { classes, cx } = useGridStyle({ autoHeight: props.autoHeight });
  const { localeText, components: localizationComponents } = useGridLocalization();
  const { rows, rowsPerPageOptions } = useRows({
    rows: props.rows,
    pinnedRow,
    rowsPerPageOptions: props.rowsPerPageOptions,
    rawModels: props.rawModels,
    pinnedRawModel: props.pinnedRawModel
  });
  const { columns, onColumnsVisibilityUpdate } = useColumns({
    columns: props.columns,
    pinnedColumns,
    visibleColumns,
    hasColumnSelection,
    isFlexible,
    onColumnsVisibilityUpdate: props.onColumnsVisibilityUpdate
  });
  const { page, pageSize, paginationMode, rowCount, onPageChange, onPageSizeChange } = useGridPaging(props);
  const { sortModel, sortingMode, onSortModelChange } = useGridSorting({ ...props, columns });
  const selectionProps = useGridRowSelection({
    rows,
    selectedItems: props?.selectedItems,
    onSelectionModelChange: props?.onSelectionModelChange,
    onSelect: props?.onSelect
  });
  const handleCellEditStart = useCallback((params, event) => {
    if (params.reason === GridCellEditStartReasons.printableKeyDown || params.reason === GridCellEditStartReasons.deleteKeyDown) {
      event.defaultMuiPrevented = true;
    }
  }, []);
  function getColumnVisibilityModel() {
    const columnVisibilityModel = props?.columnVisibilityModel ?? {};
    columns.forEach((column) => columnVisibilityModel[column.field] = !column.hide);
    return columnVisibilityModel;
  }
  return rows.length || !emptyMessage ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { id: gridId, className: classes.dataGridWrapper, style }, /* @__PURE__ */ React.createElement(
    DataGrid,
    {
      ...props,
      ...selectionProps,
      localeText,
      className: cx(
        classes.dataGridRoot,
        {
          [classes.dataGridHideHeader]: hideHeader,
          [classes.dataGridHideRows]: hideRows,
          [classes.dataGridWithPinnedRow]: !!pinnedRow
        },
        className
      ),
      rows,
      getRowId: (r) => r?.id ?? v4(),
      columns,
      sortModel,
      sortingMode,
      sortingOrder: ["desc", "asc"],
      columnVisibilityModel: getColumnVisibilityModel(),
      onSortModelChange,
      paginationModel: {
        page,
        pageSize: pinnedRow ? pageSize + 1 : pageSize
      },
      paginationMode,
      rowCount: rows?.length ?? 0,
      rowHeight: defaultRowHeight,
      disableRowSelectionOnClick: true,
      disableColumnFilter: true,
      disableColumnSelector: !hasColumnSelection,
      hideFooterPagination: true,
      hideFooterSelectedRowCount: true,
      hideFooter: true,
      disableColumnMenu: true,
      onCellEditStart: handleCellEditStart,
      slots: {
        ...slots,
        ...localizationComponents,
        baseCheckbox: ({ slotProps: _, ...props2 }) => /* @__PURE__ */ React.createElement(StyledCheckbox, { ...props2 }),
        toolbar: hasColumnSelection ? GridColumnsButton : null,
        columnsManagement: hasColumnSelection ? GridColumnsPanel : null
      },
      slotProps: {
        columnsManagement: {
          allColumns: columns?.filter((c) => c.field !== GRID_CHECKBOX_SELECTION_COL_DEF?.field),
          pinnedColumnNames: pinnedColumns,
          onColumnsVisibilityUpdate
        },
        toolbar: { noToolbarMargin }
      },
      disableColumnResize: true,
      showToolbar: hasColumnSelection
    }
  )), !hidePagination && !hideRows ? /* @__PURE__ */ React.createElement(
    StyledPagination,
    {
      count: rowCount ?? 0,
      page,
      rowsPerPage: pageSize,
      rowsPerPageOptions,
      onPageChange,
      onPageSizeChange
    }
  ) : null) : /* @__PURE__ */ React.createElement(InfoAlert, { title: emptyMessage });
};
export {
  MuiDataGrid as default
};
//# sourceMappingURL=MuiDataGrid.js.map
